/**
 * Utility functions for date operations
 * All times are UK-centric (Europe/London timezone)
 */

const UK_TIMEZONE = 'Europe/London';

/**
 * YYYY-MM-DD for the Date's calendar day in the **browser's local** timezone.
 * Use for Date values from calendar UI (`new Date(y,m,d)` or week/month helpers).
 * Avoid `toISOString().split('T')[0]`: in timezones ahead of UTC (e.g. UK BST), local
 * midnight maps to the previous UTC calendar day.
 */
export const formatLocalCalendarYmd = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * ISO weekday 1=Monday … 7=Sunday for a calendar date in a specific timezone.
 * Uses noon UTC on that YMD as anchor so the calendar day is stable across zones (avoids Fri vs Sat mix-ups).
 * @param {string} ymd - YYYY-MM-DD (the user's selected calendar date)
 * @param {string} [timezone] - IANA timezone (venue)
 * @returns {number} 1-7
 */
export const getIsoDayOfWeekForYmdInTimezone = (ymd, timezone = UK_TIMEZONE) => {
  if (!ymd || typeof ymd !== 'string') return 1;
  const parts = ymd.split('T')[0].split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return 1;
  const [y, mo, d] = parts;
  const anchor = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long'
  }).format(anchor);
  const map = {
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
    Sunday: 7
  };
  return map[weekday] ?? 1;
};

/**
 * Get 30-min time strings that have already passed for the selected date in venue timezone.
 * Used to grey out past times in the time picker.
 * @param {Date} date - The selected date (used if dateYmd not passed)
 * @param {string} [timezone] - Venue timezone (default: Europe/London)
 * @param {string|null} [dateYmd] - Selected calendar YYYY-MM-DD in venue terms (pass from UI; avoids UTC-instant vs local calendar mismatch)
 * @returns {string[]} Array of HH:MM strings for past times
 */
export const getPastTimesForDate = (date, timezone = UK_TIMEZONE, dateYmd = null) => {
  if (!date || isNaN(date.getTime())) return [];
  const now = new Date();
  const dateFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false
  });
  const timeFmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  const todayStr = dateFmt.format(now);
  const selectedStr =
    dateYmd && /^\d{4}-\d{2}-\d{2}$/.test(dateYmd.split('T')[0])
      ? dateYmd.split('T')[0]
      : dateFmt.format(date);
  if (todayStr !== selectedStr) return [];
  const timeParts = timeFmt.formatToParts(now);
  const hour = parseInt(timeParts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const minute = parseInt(timeParts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  const currentMinutes = hour * 60 + minute;
  const past = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const slotMinutes = h * 60 + m;
      if (slotMinutes < currentMinutes) {
        past.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    }
  }
  return past;
};

/**
 * Hour and minute for an instant in a given IANA timezone.
 */
export const getTimePartsInTimezone = (date, timeZone = UK_TIMEZONE) => {
  if (!date || isNaN(date.getTime())) return { hour: 0, minute: 0 };
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  const parts = formatter.formatToParts(date);
  return {
    hour: parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10),
    minute: parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10)
  };
};

/**
 * Get hour and minute from a Date in UK (Europe/London) timezone
 */
export const getUKTimeParts = (date) => getTimePartsInTimezone(date, UK_TIMEZONE);

/**
 * Calendar YYYY-MM-DD for an instant in a given IANA timezone.
 */
export const getYmdInTimezone = (date, timeZone = UK_TIMEZONE) => {
  if (!date || isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

/**
 * Interpret a naive ISO datetime (no Z / offset) as wall-clock in `timeZone`.
 * API availability blocks use local wall times without offsets; parsing as UTC shifts BST by +1h.
 */
export const parseNaiveIsoAsVenueWall = (isoString, timeZone = UK_TIMEZONE) => {
  const m = String(isoString)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const y = +m[1];
  const mo = +m[2];
  const d = +m[3];
  const h = +m[4];
  const mi = +m[5];
  const base = Date.UTC(y, mo - 1, d, 12, 0, 0);
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  for (let delta = -24 * 60; delta <= 24 * 60; delta += 1) {
    const dt = new Date(base + delta * 60 * 1000);
    const parts = fmt.formatToParts(dt);
    const py = +parts.find((p) => p.type === 'year')?.value;
    const pm = +parts.find((p) => p.type === 'month')?.value;
    const pd = +parts.find((p) => p.type === 'day')?.value;
    const ph = +parts.find((p) => p.type === 'hour')?.value;
    const pmi = +parts.find((p) => p.type === 'minute')?.value;
    if (py === y && pm === mo && pd === d && ph === h && pmi === mi) return dt;
  }
  return null;
};

/** Occasia booking/manual blocks are venue wall clock even when Postgres stamps +00:00. */
const WALL_CLOCK_BLOCK_PROVIDERS = new Set([
  'platform',
  'manual',
  'capacity_exhausted',
  'operating_hours'
]);

const stripIsoOffset = (isoString) =>
  String(isoString || '')
    .trim()
    .replace(/Z$/i, '')
    .replace(/[+-]\d{2}:?\d{2}$/, '');

/**
 * Parse block start/end from API: naive `YYYY-MM-DDTHH:mm` = venue wall time; strings with Z/offset use that instant.
 */
export const parseBlockDateTime = (isoString, timeZone = UK_TIMEZONE) => {
  if (!isoString) return null;
  const s = String(isoString).trim();
  if (!s) return null;
  if (/Z|[+-]\d{2}:?\d{2}$/.test(s)) {
    const date = new Date(s);
    return isNaN(date.getTime()) ? null : date;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
    const wall = parseNaiveIsoAsVenueWall(s, timeZone);
    if (wall) return wall;
    return safeParseDate(s);
  }
  const date = new Date(s);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Same as parseBlockDateTime, except platform/manual/OH rows with a UTC offset
 * are the stored wall clock (not a real instant). Google/Outlook keep the offset.
 */
export const parseAvailabilityBlockDateTime = (isoString, timeZone = UK_TIMEZONE, provider = null) => {
  if (!isoString) return null;
  const providerKey = String(provider || '').toLowerCase();
  const s = String(isoString).trim();
  if (WALL_CLOCK_BLOCK_PROVIDERS.has(providerKey) && /Z|[+-]\d{2}:?\d{2}$/i.test(s)) {
    const wall = parseNaiveIsoAsVenueWall(stripIsoOffset(s), timeZone);
    if (wall) return wall;
  }
  return parseBlockDateTime(isoString, timeZone);
};

export const safeParseDate = (dateString) => {
  if (!dateString) return null;
  const s = String(dateString).trim();
  if (!s) return null;
  if (!/Z|[+-]\d{2}:?\d{2}$/.test(s) && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
    return safeParseDate(s + 'Z');
  }
  const date = new Date(s);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Parse a date-only string (YYYY-MM-DD) as local date.
 * Avoids timezone shift: new Date("2026-03-07") parses as UTC midnight,
 * which can display as the previous day in timezones west of UTC.
 * @param {string} dateStr - e.g. "2026-03-07" or "2026-03-07T00:00:00Z"
 * @returns {Date|null} Local midnight for that date, or null
 */
export const parseDateOnly = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr).split('T')[0];
  const parts = s.split('-').map(Number);
  if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return null;
  const [y, m, d] = parts;
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
};

/** True when the booking's event date is before today (local calendar day). */
export const isPastBookingDate = (booking) => {
  if (!booking?.booking_date) return false;
  const bookingDate = parseDateOnly(booking.booking_date);
  if (!bookingDate || Number.isNaN(bookingDate.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return bookingDate < today;
};

/**
 * Get YYYY-MM-DD string from a Date in local timezone (for consistent comparison).
 */
export const toDateOnlyString = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export const formatDate = (date, options = {}) => {
  if (!date || isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', options);
};

export const formatTime = (timeString) => {
  if (!timeString) return '';
  return timeString.substring(0, 5);
};

export const calculateDurationHours = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  
  try {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const durationMinutes = endMinutes - startMinutes;
    
    return Math.round((durationMinutes / 60) * 10) / 10;
  } catch (error) {
    return 0;
  }
};

